import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'

export type CellEditorOption = {
  value: string
  label: string
  /** Серая подпись справа — категория у подсказки названия. */
  meta?: string | null
  /** «Возможно» — подсказка матчера, выделяется цветом. */
  hint?: boolean
}

export type CellEditorGroup = {
  label?: string
  options: CellEditorOption[]
}

export type CellEditorProps = {
  /** Ячейка, под которой открывается список. */
  anchor: HTMLElement | null
  kind: 'text' | 'number' | 'list'
  ariaLabel: string
  initialText: string
  /** Текущее значение — в списке отмечено галочкой. */
  currentValue?: string | null
  /** Для списка: можно ли печатать, чтобы отфильтровать. */
  searchable?: boolean
  placeholder?: string
  /** Варианты для запроса; для поля ввода — подсказки (пусто — списка нет). */
  loadGroups?: (query: string) => CellEditorGroup[] | Promise<CellEditorGroup[]>
  /** Меняется, когда варианты обновились извне (пришёл «Возможно») — перечитать список. */
  optionsKey?: string
  onCommit: (value: string) => void
  onCancel: () => void
  /** Tab / Shift+Tab: сохранить и перейти к соседней ячейке. value = null — без изменений. */
  onTab?: (direction: 1 | -1, value: string | null) => void
}

/**
 * Быстрое редактирование ячейки «Моих прайсов». Один компонент на все поля, чтобы списки
 * поставщика, единицы и категории выглядели и вели себя одинаково: Enter — сохранить,
 * Esc — отменить, ↑↓ — выбор в списке, Tab — к следующей ячейке.
 *
 * Список рисуется в портале поверх страницы: ячейка таблицы обрезает всё, что выходит за неё.
 */
export const CellEditor = ({
  anchor,
  kind,
  ariaLabel,
  initialText,
  currentValue = null,
  searchable = true,
  placeholder,
  loadGroups,
  optionsKey = '',
  onCommit,
  onCancel,
  onTab,
}: CellEditorProps) => {
  // Родитель создаёт loadGroups на каждый рендер; держим последнюю в ref, иначе список
  // перезапрашивался бы при любом обновлении таблицы.
  const loadRef = useRef(loadGroups)
  useLayoutEffect(() => {
    loadRef.current = loadGroups
  }, [loadGroups])
  const hasLoader = loadGroups != null
  const inputRef = useRef<HTMLInputElement>(null)
  // После Enter/Esc ячейка размонтирует редактор, поле теряет фокус, и onBlur сохранил бы
  // значение второй раз. Завершаем ровно один раз.
  const doneRef = useRef(false)
  const commit = (value: string) => {
    if (doneRef.current) return
    doneRef.current = true
    onCommit(value)
  }
  const cancel = () => {
    if (doneRef.current) return
    doneRef.current = true
    onCancel()
  }
  const listId = useId()
  const [text, setText] = useState(initialText)
  const [groups, setGroups] = useState<CellEditorGroup[]>([])
  const [active, setActive] = useState(-1)
  const [position, setPosition] = useState<{ left: number; top: number; width: number } | null>(null)
  const isList = kind === 'list'
  const query = isList && (!searchable || text === initialText) ? '' : text

  useEffect(() => {
    const input = inputRef.current
    if (!input) return
    // preventScroll: иначе браузер подкручивает таблицу, чтобы поле не ушло под закреплённую колонку.
    input.focus({ preventScroll: true })
    if (!isList) input.select()
  }, [isList])

  useEffect(() => {
    const load = loadRef.current
    if (!load) {
      setGroups([])
      return
    }
    if (!isList && (query.trim().length < 2 || query === initialText)) {
      setGroups([])
      return
    }
    let cancelled = false
    const timer = window.setTimeout(
      () => {
        void Promise.resolve(load(query))
          .then((next) => {
            if (!cancelled) {
              setGroups(next)
              setActive(-1)
            }
          })
          .catch(() => {
            if (!cancelled) setGroups([])
          })
      },
      isList ? 0 : 200,
    )
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [query, isList, initialText, hasLoader, optionsKey])

  const options = groups.flatMap((group) => group.options)
  const listOpen = isList || options.length > 0

  const reposition = useCallback(() => {
    if (!anchor) return
    const rect = anchor.getBoundingClientRect()
    setPosition({
      left: window.scrollX + rect.left + 8,
      top: window.scrollY + rect.bottom - 6,
      width: Math.max(kind === 'text' ? 420 : 240, rect.width - 16),
    })
  }, [anchor, kind])

  useLayoutEffect(() => {
    if (!listOpen) return
    reposition()
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, true)
    return () => {
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
    }
  }, [listOpen, reposition])

  const pickedValue = (): string | null => {
    if (active >= 0 && options[active]) return options[active].value
    if (isList) return query.trim() && options[0] ? options[0].value : null
    return text
  }

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      cancel()
      return
    }
    if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && options.length > 0) {
      event.preventDefault()
      setActive((index) => (index + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length)
      return
    }
    if (event.key === 'Enter') {
      event.preventDefault()
      const value = pickedValue()
      if (value == null) cancel()
      else commit(value)
      return
    }
    if (event.key === 'Tab' && onTab) {
      event.preventDefault()
      if (doneRef.current) return
      doneRef.current = true
      onTab(event.shiftKey ? -1 : 1, isList ? null : text)
    }
  }

  const onBlur = () => {
    window.setTimeout(() => {
      if (document.activeElement === inputRef.current) return
      if (isList) cancel()
      else commit(text)
    }, 0)
  }

  let flatIndex = 0
  const list =
    listOpen && position
      ? createPortal(
          <ul
            id={listId}
            role="listbox"
            className="filter-combobox__list market-cell-editor__list"
            style={{ left: position.left, top: position.top, width: position.width }}
            onMouseDown={(event) => event.preventDefault()}
          >
            {options.length === 0 ? (
              <li className="filter-combobox__option echat-muted">Ничего не нашлось</li>
            ) : (
              groups.map((group, groupIndex) => (
                <li key={group.label ?? `group-${groupIndex}`} role="presentation" className="market-cell-editor__group">
                  {group.label ? <span className="market-cell-editor__label">{group.label}</span> : null}
                  <ul role="group" className="market-cell-editor__options">
                    {group.options.map((option) => {
                      const index = flatIndex++
                      const selected = option.value === currentValue
                      return (
                        <li
                          key={`${group.label ?? ''}:${option.value}`}
                          role="option"
                          aria-selected={selected}
                          className={[
                            'filter-combobox__option',
                            'market-cell-editor__option',
                            option.hint ? 'market-cell-editor__option--hint' : null,
                            index === active ? 'is-active' : null,
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          onClick={() => commit(option.value)}
                        >
                          <span className="market-cell-editor__text">{highlight(option.label, isList ? '' : query)}</span>
                          {option.meta ? <span className="market-cell-editor__meta">{option.meta}</span> : null}
                          {selected ? <CheckIcon /> : null}
                        </li>
                      )
                    })}
                  </ul>
                </li>
              ))
            )}
          </ul>,
          document.body,
        )
      : null

  return (
    <>
      <input
        ref={inputRef}
        className={`echat-field__input market-cell-editor__input${isList ? ' market-cell-editor__input--list' : ''}${kind === 'number' ? ' market-cell-editor__input--number' : ''}`}
        aria-label={ariaLabel}
        value={text}
        placeholder={placeholder}
        inputMode={kind === 'number' ? 'decimal' : undefined}
        readOnly={isList && !searchable}
        role={listOpen ? 'combobox' : undefined}
        aria-expanded={listOpen ? true : undefined}
        aria-controls={listOpen ? listId : undefined}
        aria-autocomplete={hasLoader ? 'list' : undefined}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        onClick={(event) => event.stopPropagation()}
      />
      {list}
    </>
  )
}

/** Жирным — совпавшие начала слов: «сыр рос» → **Сыр** **Рос**сийский. */
const highlight = (label: string, query: string) => {
  const tokens = query
    .toLowerCase()
    .replace(/ё/g, 'е')
    .split(/\s+/)
    .filter((token) => token.length >= 2)
  if (tokens.length === 0) return label
  return label.split(/(\s+)/).map((part, index) => {
    const normalized = part.toLowerCase().replace(/ё/g, 'е')
    const token = tokens.find((candidate) => normalized.startsWith(candidate))
    if (!token) return part
    return (
      <span key={index}>
        <strong className="market-cell-editor__match">{part.slice(0, token.length)}</strong>
        {part.slice(token.length)}
      </span>
    )
  })
}

const CheckIcon = () => (
  <svg className="market-cell-editor__check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 6 9 17l-5-5" />
  </svg>
)
